import { create, v, w } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import theme from '@dojo/widgets/middleware/theme';
import i18n from '@dojo/framework/core/middleware/i18n';
import commonBundle from './nls';
import { uuid } from '@dojo/framework/core/util';
import { formatAriaProperties, Keys } from '@dojo/widgets/common/util';
import { GlobalEvent } from '@dojo/widgets/global-event';
import { inert } from '@dojo/framework/core/middleware/inert';

import Icon from '../icon';

import { bodyScroll } from '../middleware/body-scroll';
import * as animations from '@dojo/widgets/common/styles/animations.m.css';
import fixedCss from '../css/sliding-pane-fixed.m.css';
import css from '../css/sliding-pane.m.css';

/**
 * Enum for left / right alignment
 */
export type Align = 'bottom' | 'left' | 'right' | 'top';

export interface SlidingPaneProperties {
	/** The position of the pane on the screen */
	align?: Align;
	/** Custom aria attributes */
	aria?: { [key: string]: string | null };
	/** Hidden text used by screen readers to display for the close button */
	closeText?: string;

	/** Called when the pane is swiped closed or the underlay is clicked or tapped */
	onRequestClose?(): void;

	/** Determines whether the pane is open or closed */
	open?: boolean;
	/** Title to display in the pane */
	title?: string;
	/** Determines whether a semi-transparent background shows behind the pane */
	underlay?: boolean;
	/** Width of the pane in pixels */
	width?: number;
	/** optional key */
	key?: string | number;
	xqa?: string;
}

/**
 * The default width of the slide pane
 */
const DEFAULT_WIDTH = 320;

enum Plane {
	x,
	y
}

enum Slide {
	in,
	out
}

export interface SlidingPaneICache {
	initialPosition: number;
	slide: Slide | undefined;
	swiping: boolean | undefined;
	titleId: string;
	transform: number;
	hasMoved: boolean;
	open: boolean;
}

const factory = create({
	icache: createICacheMiddleware<SlidingPaneICache>(),
	bodyScroll,
	inert,
	theme,
	i18n
}).properties<SlidingPaneProperties>();

export const SlidingPane = factory(
	({ middleware: { icache, bodyScroll, inert, theme, i18n }, properties, children }) => {
		let { closeText } = properties();
		const {
			aria = {},
			open = false,
			title = '',
			align = 'left',
			width = DEFAULT_WIDTH,
			underlay = false,
			key = 'slidingpane',
			xqa = 'sliding-pane'
		} = properties();
		const themeCss = theme.classes(css);

		if (icache.get('open') !== open) {
			const wasOpen = icache.get('open');
			icache.set('open', open);

			if (open && !wasOpen) {
				icache.set('slide', Slide.in);
			} else if (!open && wasOpen) {
				icache.set('slide', Slide.out);
			}
		}

		inert.set(key, open, true);

		const plane = align === 'left' || align === 'right' ? Plane.x : Plane.y;

		let translate = '';
		const translateAxis = plane === Plane.x ? 'X' : 'Y';

		if (icache.get('swiping')) {
			translate =
				align === 'left' || align === 'top'
					? `-${icache.getOrSet('transform', 0)}`
					: `${icache.getOrSet('transform', 0)}`;
		}

		const contentStyles = {
			transform: translate ? `translate${translateAxis}(${translate}%)` : undefined,
			width: plane === Plane.x ? `${width}px` : undefined,
			height: plane === Plane.y ? `${width}px` : undefined
		};

		const alignCss: { [key: string]: any } = themeCss;
		const slide = icache.get('slide');

		const contentClasses = [
			alignCss[align],
			open ? themeCss.open : null,
			slide === Slide.in ? themeCss.slideIn : null,
			slide === Slide.out ? themeCss.slideOut : null
		];

		const fixedAlignCss: { [key: string]: any } = fixedCss;
		const fixedContentClasses = [fixedAlignCss[`${align}Fixed`]];

		if (!closeText) {
			const { messages } = i18n.localize(commonBundle);
			closeText = `${messages.close} ${title}`;
		}

		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const getDelta = (event: PointerEvent, eventType: string) => {
			const { align = 'left' } = properties();
			const plane = align === 'left' || align === 'right' ? Plane.x : Plane.y;

			if (plane === Plane.x) {
				const currentX = event.pageX;
				return align === 'right'
					? currentX - icache.getOrSet('initialPosition', 0)
					: icache.getOrSet('initialPosition', 0) - currentX;
			} else {
				const currentY = event.pageY;
				return align === 'bottom'
					? currentY - icache.getOrSet('initialPosition', 0)
					: icache.getOrSet('initialPosition', 0) - currentY;
			}
		};

		const onCloseClick = (event: MouseEvent) => {
			event.stopPropagation();
			const { onRequestClose } = properties();
			onRequestClose && onRequestClose();
		};

		const onUnderlayMouseUp = () => {
			const { onRequestClose } = properties();
			if (icache.getOrSet('hasMoved', false) === false) {
				onRequestClose && onRequestClose();
			}
		};

		const titleId = icache.getOrSet('titleId', () => uuid());

		bodyScroll(!open);

		const keyup = (event: KeyboardEvent) => {
			event.stopPropagation();
			if (event.which === Keys.Escape) {
				const { onRequestClose } = properties();
				onRequestClose && onRequestClose();
			}
		};
		const paneContent = () => {
			return open
				? [
						w(GlobalEvent, { key: 'global', document: { keyup } }, []),
						v('div', {
							key: 'underlay',
							classes: [underlay ? themeCss.underlayVisible : null, fixedCss.underlay],
							enterAnimation: animations.fadeIn,
							exitAnimation: animations.fadeOut,
							onpointerup: onUnderlayMouseUp
						}),
						v(
							'div',
							{
								...formatAriaProperties(aria),
								key: 'content',
								classes: [themeCss.pane, ...contentClasses, fixedCss.paneFixed, ...fixedContentClasses],
								styles: contentStyles,
								focus: true,
								xqa
							},
							[
								title
									? v('div', { classes: [themeCss.title], key: 'title' }, [
											v(
												'div',
												{
													id: titleId,
													role: 'heading',
													'aria-level': '1',
													classes: [themeCss.titleContent]
												},
												[title]
											),
											v(
												'button',
												{ classes: [themeCss.close], type: 'button', onclick: onCloseClick },
												[
													closeText,
													v('span', { classes: [themeCss.closeIcon] }, [
														w(Icon, { faname: 'times' })
													])
												]
											)
									  ])
									: null,
								v('div', { classes: [themeCss.content, fixedCss.contentFixed] }, children())
							]
						)
				  ]
				: [];
		};

		return v('body', {}, [
			open
				? v(
						'div',
						{
							key: `${key}-open`,
							'aria-labelledby': titleId,
							'aria-modal': 'true',
							role: 'dialog',
							classes: [theme.variant(), themeCss.root]
						},
						paneContent()
				  )
				: v(
						'div',
						{
							key: `${key}-closed`,
							'aria-hidden': 'true',
							classes: [theme.variant(), themeCss.root]
						},
						paneContent()
				  )
		]);
	}
);

export default SlidingPane;
